import { forwardRef, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { selectAppliedInferenceSet, selectClient, selectSessionAdvisor } from '../model';
import styles from './OnboardingPdfReportV2.module.css';
import { FileText, BarChart3, TrendingUp, ShieldCheck, Banknote, Coins } from 'lucide-react';
import {
  careEnvironmentDefs,
  carePhaseDefs,
  formatCurrency,
  formatLtcCostDisplay,
  formatDurationYears,
  formatInteger,
  formatPercent,
  formatThousands,
  fundingPolicyTypeDefs,
  getLtcCostRoundingStep,
  isCarePhasePast,
  FundingPolicyType,
  isNullOrUndefined,
  isDurationCustom,
  checkPolicyCalculationTypeEquals,
  CarePhase,
  calcFutureValue,
  calcCapitalGainsTaxLiability,
  calcFutureValueOfMonthlyContributions,
} from '@shared';
import { cn } from '@/lib/utils';
import { isInferenceEdited } from '../util';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PRODUCTS_BY_POLICY_TYPE } from '@client/lib/nyc-lif-demo';
import { useGuidedPlanningSession } from '@client/features/guided-financial-planning';

import CompanyLogoContainer from '../components/CompanyLogo';

// Helper to calculate widths for the progress bar
function calculatePhaseWidths(phases: { cost: number }[]) {
  const total = phases.reduce((acc, curr) => acc + curr.cost, 0);
  if (total === 0) return phases.map(() => 0);
  return phases.map(p => (p.cost / total) * 100);
}

function getSelfFundingName(name?: string | null) {
  const defaultName = 'Self-funding';
  return !name || name === defaultName ? defaultName : `${defaultName} (${name})`;
}

function getRoiValue(
  source: SelfFundingSource | AnnuitySource | PolicyFundingSource | FundingSources,
  displayType?: PdfRoiDisplayType,
): number | undefined {
  const type = displayType || 'totalROI';

  if ('selfFundingProjectedReturnOnInvestment' in source) {
    const s = source as SelfFundingSource;
    return type === 'IRR'
      ? s.selfFundingProjectedInternalRateOfReturn
      : type === 'CAGR'
        ? s.selfFundingProjectedCompoundAnnualGrowthRate
        : s.selfFundingProjectedReturnOnInvestment;
  }

  if ('annuityProjectedReturnOnInvestment' in source) {
    const a = source as AnnuitySource;
    return type === 'IRR'
      ? a.annuityProjectedInternalRateOfReturn
      : type === 'CAGR'
        ? a.annuityProjectedCompoundAnnualGrowthRate
        : a.annuityProjectedReturnOnInvestment;
  }

  if ('policyProjectedReturnOnInvestment' in source) {
    const p = source as PolicyFundingSource;
    return type === 'IRR'
      ? p.policyProjectedInternalRateOfReturn
      : type === 'CAGR'
        ? p.policyProjectedCompoundAnnualGrowthRate
        : p.policyProjectedReturnOnInvestment;
  }

  const f = source as FundingSources;
  return type === 'IRR'
    ? f.combinedProjectedInternalRateOfReturn
    : type === 'CAGR'
      ? f.combinedProjectedCompoundAnnualGrowthRate
      : f.combinedProjectedReturnOnInvestment;
}

export const OnboardingPdfReportV2 = forwardRef<HTMLDivElement>((props, ref) => {
  const client = useSelector(selectClient)!;
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { flags } = useFeatureFlags();
  const { ltcLikelihoodEver, ltcAtAge } = useSelector(selectAppliedInferenceSet);

  const {
    allPhaseCosts: {
      allPhaseCareHoursProvided,
      allPhaseFamilyCareHoursProvided,
      allPhaseFamilyProvidedPercent,
      allPhaseProfessionalCareHoursProvided,
      allPhaseInflatedProfessionalShareCost,
      allPhaseCareMonthsNeeded,
    },
    fundingSources: {
      annuity: {
        hasAnnuity,
        annuityAnnualPayOut,
        annuityAnnualPayoutForLtc,
        annuityAnnualPayoutForLtcPeriodYears,
        annuityExpectedPaymentEndAge,
        annuityExpectedPaymentStartAge,
        annuityPurchasePrice,
        annuityProjectedReturnOnInvestment,
        annuityTotalValue,
        annuityProjectedCostCoverage,
        annuityNonLtcPayout,
        isDeactivated: annuityIsDeactivated,
      },
      combinedFundingTotalCost,
      combinedProjectedCostCoverage,
      combinedProjectedReturnOnInvestment,
      combinedTotalValue,
      combinedProjectedCostCoveragePercent,
      combinedNonLtcPayout,
    },
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    phaseCosts,
  } = client;

  const roundingStep = flags.ltcCostRoundingOptions
    ? getLtcCostRoundingStep(client?.clientAdvisorSettings?.ltcCostDisplayPrecision)
    : 0;
  const formatLtcCost = (value: number) =>
    formatLtcCostDisplay(value, { roundingStep }).formattedDisplay;

  const effectiveDiscountRateForCagr =
    client.clientCalculationSettings?.discountRate ??
    sessionAdvisor?.defaultDiscountRateForCagr ??
    undefined;
  const effectiveRoiDisplayType: PdfRoiDisplayType | undefined =
    sessionAdvisor?.pdfRoiDisplayType === 'CAGR' &&
    (effectiveDiscountRateForCagr === undefined || effectiveDiscountRateForCagr === null)
      ? 'totalROI'
      : sessionAdvisor?.pdfRoiDisplayType;
  const cagrDiscountRateLabel =
    effectiveDiscountRateForCagr !== undefined && effectiveDiscountRateForCagr !== null
      ? ` (${Number(effectiveDiscountRateForCagr) === Math.round(Number(effectiveDiscountRateForCagr)) ? Math.round(Number(effectiveDiscountRateForCagr)) : Number(effectiveDiscountRateForCagr).toFixed(1)}% discount rate)`
      : '';

  const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
  const anyCustomDurations = isDurationCustom(phaseCosts);

  // Filter Active Policies
  const ltcPolicies = Object.values(multipleFundingSources.ltcPolicy).filter(
    ltcPolicy => !ltcPolicy.isDeactivated,
  );

  const { session: guidedPlanningSession } = useGuidedPlanningSession(client.clientId);

  // Filter Active Self Funding keeping IDs for guided planning allocations
  const selfFundingSources = Object.entries(multipleFundingSources.selfFunding)
    .filter(([, selfFunding]) => {
      if (selfFunding.isDeactivated) return false;

      const areSelfFundingFieldsPopulated =
        (selfFunding.annualRateOfReturn && selfFunding.annualRateOfReturn > 0) ||
        (selfFunding.monthlyContributionAmount && selfFunding.monthlyContributionAmount > 0) ||
        (selfFunding.existingAssetsValue && selfFunding.existingAssetsValue > 0);

      return selfFunding.hasSelfFunding || areSelfFundingFieldsPopulated;
    })
    .map(([id, data]) => ({ id, data }));

  const guidedFundingAllocations =
    guidedPlanningSession?.stepData?.planComparisonSnapshot?.fundingAllocations ?? [];
  const allocationBySourceId = guidedFundingAllocations.reduce(
    (acc, allocation) => {
      acc[allocation.selfFundingSourceId] = allocation;
      return acc;
    },
    {} as Record<string, { amount: number; existingValue?: number }>,
  );

  // Extract costs for the phases for the progress bar
  const phase1 = phaseCosts.find(p => p.carePhase === CarePhase.earlyCare);
  const phase2 = phaseCosts.find(p => p.carePhase === CarePhase.moderateCare);
  const phase3 = phaseCosts.find(p => p.carePhase === CarePhase.fullCare);

  const phaseOneCost = phase1?.phaseInflatedProfessionalShareCost ?? 0;
  const phaseTwoCost = phase2?.phaseInflatedProfessionalShareCost ?? 0;
  const phaseThreeCost = phase3?.phaseInflatedProfessionalShareCost ?? 0;

  const phaseWidths = calculatePhaseWidths([
    { cost: phaseOneCost },
    { cost: phaseTwoCost },
    { cost: phaseThreeCost },
  ]);

  const allFundingSources = [
    ...selfFundingSources.map(s => ({ type: 'self-funding' as const, data: s.data, id: s.id })),
    ...(hasAnnuity && !annuityIsDeactivated
      ? [{ type: 'annuity' as const, data: client.fundingSources.annuity }]
      : []),
    ...ltcPolicies.map(p => ({ type: 'policy' as const, data: p })),
  ];

  const sourceChunks: (typeof allFundingSources)[] = [];
  for (let i = 0; i < allFundingSources.length; i += 2) {
    sourceChunks.push(allFundingSources.slice(i, i + 2));
  }

  return (
    <div
      ref={ref}
      className={styles.PdfContainer}
      id="client-pdf-report"
    >
      <table className={styles.printTable}>
        <thead>
          <tr>
            <td>
              <div className={styles.pageHeaderSpacer} />
            </td>
          </tr>
        </thead>
        <tfoot>
          <tr>
            <td>
              <div className={styles.pageFooterSpacer} />
              <FundingDisclosure clientFirstName={client.clientFirstName} />
              <div className={styles.pageFooterSafetySpacer} />
            </td>
          </tr>
        </tfoot>
        <tbody>
          <tr>
            <td>
              {/* HEADER & PREDICTIONS CARD */}
              <div className={styles.headerCard}>
                {/* WHITE LABEL & HEADER */}
                <div className={styles.headerContainer}>
                  <div>
                    {/* White Label Integration */}
                    <CompanyLogoContainer
                      organizationDisplayName={sessionAdvisor.organizationDisplayName}
                      containerClassName={styles.headerLogoContainer}
                      organizationNameClassName={styles.headerOrgName}
                    />
                    <div className={styles.reportTitle}>
                      <FileText size={16} />
                      Long-Term Care Financial Report
                    </div>
                    <h1 className={styles.clientName}>
                      {client.clientFirstName} {client.clientLastName}
                    </h1>
                    <p className={styles.reportDescription}>
                      Welcome to your long-term care plan report. This document provides a
                      comprehensive overview of your current financial care plan for you and your
                      family.
                    </p>
                  </div>
                  <div className={styles.reportDate}>
                    <div className={styles.reportDateLabel}>Report Date</div>
                    <div className={styles.reportDateValue}>
                      {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              </div>

              {/* PREDICTIONS */}
              <div className={cn(styles.predictionsGrid, 'grid grid-cols-2 gap-4 md:grid-cols-4')}>
                {!clientHasStartedLtc && (
                  <div
                    className={cn(
                      styles.predictionCard,
                      styles.cardPurple,
                      'rounded-xl border p-5 text-center',
                    )}
                  >
                    <div
                      className={cn(
                        styles.predictionLabel,
                        'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                      )}
                    >
                      CARE LIKELIHOOD
                    </div>
                    <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                      {formatPercent(ltcLikelihoodEver, 0)}
                    </div>
                    <div
                      className={cn(
                        styles.predictionSubtitle,
                        'text-muted-foreground mt-1 text-xs',
                      )}
                    >
                      National Avg: 56%
                    </div>
                  </div>
                )}
                {!clientHasStartedLtc && (
                  <div
                    className={cn(
                      styles.predictionCard,
                      styles.cardCyan,
                      'rounded-xl border p-5 text-center',
                    )}
                  >
                    <div
                      className={cn(
                        styles.predictionLabel,
                        'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                      )}
                    >
                      START AGE
                    </div>
                    <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                      {ltcAtAge} yrs
                    </div>
                    {isInferenceEdited(client, 'ltcAtAge') && (
                      <div
                        className={cn(
                          styles.predictionSubtitle,
                          'text-muted-foreground mt-1 text-xs',
                        )}
                      >
                        (edited)
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={cn(
                    styles.predictionCard,
                    styles.cardLightGreen,
                    'rounded-xl border p-5 text-center',
                  )}
                >
                  <div
                    className={cn(
                      styles.predictionLabel,
                      'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                    )}
                  >
                    {clientHasStartedLtc ? 'REMAINING CARE' : 'CARE DURATION'}
                  </div>
                  <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                    {formatDurationYears(ltcDurationYears)} yrs
                  </div>
                  {anyCustomDurations && (
                    <div
                      className={cn(
                        styles.predictionSubtitle,
                        'text-muted-foreground mt-1 text-xs',
                      )}
                    >
                      (edited)
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    styles.predictionCard,
                    styles.cardGray,
                    'rounded-xl border p-5 text-center',
                  )}
                >
                  <div
                    className={cn(
                      styles.predictionLabel,
                      'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                    )}
                  >
                    TOTAL HOURS
                  </div>
                  <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                    {formatThousands(allPhaseCareHoursProvided)}
                  </div>
                </div>
              </div>

              {/* PROJECTED CARE COSTS */}
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <BarChart3 size={20} />
                </div>
                <div className={styles.sectionTitle}>Projected Care Costs</div>
              </div>

              <div className={styles.costsContainer}>
                <div className={styles.totalCostRow}>
                  <div className={styles.totalCostLabel}>
                    {client.clientFirstName}'s {clientHasStartedLtc ? 'remaining' : 'projected'}{' '}
                    costs
                  </div>
                  <div className={styles.totalCostValue}>
                    {formatLtcCost(allPhaseInflatedProfessionalShareCost)}
                  </div>
                </div>

                <div className={styles.phasesGrid}>
                  {phase1 && (
                    <div className={cn(styles.phaseCard, styles.phaseCardHome)}>
                      <div className={styles.phaseCardHeader}>
                        <div
                          className={cn(
                            styles.phaseLabel,
                            'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                          )}
                        >
                          {carePhaseDefs[phase1.carePhase].label.toUpperCase()}
                        </div>
                        <div
                          className={cn(styles.phaseValue, 'text-primary mb-1 text-2xl font-bold')}
                        >
                          {formatLtcCost(phaseOneCost)}
                        </div>
                        <div className={cn(styles.phaseSub, 'text-muted-foreground text-xs')}>
                          {isCarePhasePast(phase1.carePhase, client)
                            ? 'Past'
                            : careEnvironmentDefs[phase1.appliedCareEnvironment].label}
                        </div>
                      </div>
                      <div className={styles.phaseDetails}>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Cost Today:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatLtcCost(phase1.phaseProfessionalShareCost ?? 0)}
                          </span>
                        </div>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Future Cost:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatLtcCost(phaseOneCost)}
                          </span>
                        </div>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Hours:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatThousands(phase1.phaseCareHoursProvided ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {phase2 && (
                    <div className={cn(styles.phaseCard, styles.phaseCardMod)}>
                      <div className={styles.phaseCardHeader}>
                        <div
                          className={cn(
                            styles.phaseLabel,
                            'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                          )}
                        >
                          {carePhaseDefs[phase2.carePhase].label.toUpperCase()}
                        </div>
                        <div
                          className={cn(styles.phaseValue, 'text-primary mb-1 text-2xl font-bold')}
                        >
                          {formatLtcCost(phaseTwoCost)}
                        </div>
                        <div className={cn(styles.phaseSub, 'text-muted-foreground text-xs')}>
                          {isCarePhasePast(phase2.carePhase, client)
                            ? 'Past'
                            : careEnvironmentDefs[phase2.appliedCareEnvironment].label}
                        </div>
                      </div>
                      <div className={styles.phaseDetails}>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Cost Today:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatLtcCost(phase2.phaseProfessionalShareCost ?? 0)}
                          </span>
                        </div>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Future Cost:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatLtcCost(phaseTwoCost)}
                          </span>
                        </div>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Hours:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatThousands(phase2.phaseCareHoursProvided ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {phase3 && (
                    <div className={cn(styles.phaseCard, styles.phaseCardFull)}>
                      <div className={styles.phaseCardHeader}>
                        <div
                          className={cn(
                            styles.phaseLabel,
                            'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                          )}
                        >
                          {carePhaseDefs[phase3.carePhase].label.toUpperCase()}
                        </div>
                        <div
                          className={cn(styles.phaseValue, 'text-primary mb-1 text-2xl font-bold')}
                        >
                          {formatLtcCost(phaseThreeCost)}
                        </div>
                        <div className={cn(styles.phaseSub, 'text-muted-foreground text-xs')}>
                          {isCarePhasePast(phase3.carePhase, client)
                            ? 'Past'
                            : careEnvironmentDefs[phase3.appliedCareEnvironment].label}
                        </div>
                      </div>
                      <div className={styles.phaseDetails}>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Cost Today:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatLtcCost(phase3.phaseProfessionalShareCost ?? 0)}
                          </span>
                        </div>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Future Cost:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatLtcCost(phaseThreeCost)}
                          </span>
                        </div>
                        <div className={cn(styles.phaseDetailItem)}>
                          <span className={styles.phaseDetailLabel}>Hours:</span>
                          <span className={styles.phaseDetailValue}>
                            {formatThousands(phase3.phaseCareHoursProvided ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.progressBarContainer}>
                  <div className={styles.progressBar}>
                    <div
                      style={{ width: `${phaseWidths[0]}%` }}
                      className={cn(styles.progressSegmentEarly, 'transition-all')}
                    />
                    <div
                      style={{ width: `${phaseWidths[1]}%` }}
                      className={cn(styles.progressSegmentModerate, 'transition-all')}
                    />
                    <div
                      style={{ width: `${phaseWidths[2]}%` }}
                      className={cn(styles.progressSegmentFull, 'transition-all')}
                    />
                  </div>
                </div>
              </div>

              {/* CARE HOURS DISTRIBUTION */}
              <div className={styles.careHoursContainer}>
                <h3 className={styles.careHoursTitle}>
                  {clientHasStartedLtc ? 'Remaining Care Hours' : 'Total Care Hours'} Distribution
                </h3>
                <div className={styles.hoursDistribution}>
                  <div>
                    <div className={styles.hoursRow}>
                      <div className={styles.hoursLabel}>Professional Care Services</div>
                      <div className={styles.hoursValue}>
                        {formatThousands(allPhaseProfessionalCareHoursProvided)} hrs
                      </div>
                    </div>
                    <div className={styles.hoursBarBg}>
                      <div
                        className={cn(styles.hoursBarFill, styles.barPurple)}
                        style={{
                          width: `${(allPhaseProfessionalCareHoursProvided / allPhaseCareHoursProvided) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className={styles.hoursRow}>
                      <div className={styles.hoursLabel}>Family Provided Care</div>
                      <div className={styles.hoursValue}>
                        {formatThousands(allPhaseFamilyCareHoursProvided)} hrs
                      </div>
                    </div>
                    <div className={styles.hoursBarBg}>
                      <div
                        className={cn(styles.hoursBarFill, styles.barPurple)}
                        style={{
                          width: `${(allPhaseFamilyCareHoursProvided / allPhaseCareHoursProvided) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Funding Strategy on new page (outside table so print break rules apply) */}
      <div className={cn('pt-2', styles.pageBreakBefore, styles.fundingStrategySection)}>
        <table className={styles.printTable}>
          <thead>
            <tr>
              <td>
                <div className={styles.pageHeaderSpacer} />
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>
                    <TrendingUp size={20} />
                  </div>
                  <div className={styles.sectionTitle}>Funding Strategy</div>
                </div>

                <div className="flex flex-col gap-4">
                  {sourceChunks.map((chunk, chunkIdx) => (
                    <div
                      key={`chunk-${chunkIdx}`}
                      className={styles.fundingGrid}
                    >
                      {chunk.map((source, idx) => {
                        if (source.type === 'self-funding') {
                          const s = source.data;
                          const allocation = allocationBySourceId[source.id];
                          const originalExistingAssetsValue =
                            guidedPlanningSession?.stepData?.multipleFundingSourcesSnapshot
                              ?.selfFunding?.[source.id]?.existingAssetsValue ??
                            allocation?.existingValue ??
                            s.existingAssetsValue;
                          const assetsUsedForPolicy = allocation?.amount;
                          const remainingPrincipal = Math.max(
                            0,
                            (originalExistingAssetsValue ?? 0) - (assetsUsedForPolicy ?? 0),
                          );
                          const futureValueOfRemainingPrincipal = isNullOrUndefined(
                            s.annualRateOfReturn,
                          )
                            ? 0
                            : calcFutureValue(
                                remainingPrincipal,
                                s.annualRateOfReturn ?? 0,
                                client.appliedInferenceSet.yearsTillLtc,
                              );
                          const netFutureValueOfRemainingPrincipal =
                            futureValueOfRemainingPrincipal -
                            calcCapitalGainsTaxLiability(
                              remainingPrincipal,
                              s.gainsTaxRate,
                              futureValueOfRemainingPrincipal,
                            );
                          const futureValueOfMonthlyContributions = isNullOrUndefined(
                            s.monthlyContributionAmount,
                          )
                            ? 0
                            : calcFutureValueOfMonthlyContributions(
                                s.monthlyContributionAmount ?? 0,
                                s.annualRateOfReturn ?? 0,
                                s.contributionMonthCount ?? 0,
                              );
                          const netFutureValueOfMonthlyContributions =
                            futureValueOfMonthlyContributions -
                            calcCapitalGainsTaxLiability(
                              (s.monthlyContributionAmount ?? 0) * (s.contributionMonthCount ?? 0),
                              s.gainsTaxRate,
                              futureValueOfMonthlyContributions,
                            );
                          const remainingProjectedLtcPayout =
                            netFutureValueOfRemainingPrincipal +
                            netFutureValueOfMonthlyContributions +
                            (s.monthlyIncomeTotal ?? 0);
                          return (
                            <div
                              key={`self-funding-${idx}`}
                              className={styles.fundingCard}
                            >
                              <div className={styles.fundingHeaderSection}>
                                <div className={styles.fundingCardHeader}>
                                  <div className={styles.sectionIcon}>
                                    <TrendingUp size={16} />
                                  </div>
                                  <h3 className={styles.fundingCardTitle}>
                                    {getSelfFundingName(s.selfFundingName)}
                                  </h3>
                                </div>

                                <div
                                  className={cn(
                                    styles.fundingHeaderGrid,
                                    styles.fundingHeaderGridThree,
                                  )}
                                >
                                  <div className={styles.fundingHeaderItem}>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderLabel,
                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                      )}
                                    >
                                      Initial Assets
                                    </div>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderValue,
                                        styles.primaryValue,
                                        'text-2xl font-bold',
                                      )}
                                    >
                                      {formatCurrency(originalExistingAssetsValue ?? 0)}
                                    </div>
                                  </div>
                                  <div className={styles.fundingHeaderItem}>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderLabel,
                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                      )}
                                    >
                                      Assets Used to Fund LTC Policy
                                    </div>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderValue,
                                        'text-2xl font-bold',
                                      )}
                                    >
                                      {isNullOrUndefined(assetsUsedForPolicy)
                                        ? '-'
                                        : formatCurrency(assetsUsedForPolicy)}
                                    </div>
                                  </div>
                                  <div className={styles.fundingHeaderItem}>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderLabel,
                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                      )}
                                    >
                                      Projected Remaining LTC Payout
                                    </div>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderValue,
                                        styles.primaryValue,
                                        'text-2xl font-bold',
                                      )}
                                    >
                                      {formatLtcCost(remainingProjectedLtcPayout ?? 0)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className={styles.fundingRowsContainer}>
                                <FundingRow
                                  label="Today's Assets for LTC"
                                  value={
                                    isNullOrUndefined(s.existingAssetsValue)
                                      ? '-'
                                      : formatCurrency(s.existingAssetsValue)
                                  }
                                />
                                {!clientHasStartedLtc && (
                                  <>
                                    <FundingRow
                                      label="Monthly Contribution"
                                      value={
                                        isNullOrUndefined(s.monthlyContributionAmount)
                                          ? '-'
                                          : `${formatCurrency(s.monthlyContributionAmount)} over ${s.contributionYearCount} year${s.contributionYearCount === 1 ? '' : 's'}`
                                      }
                                    />
                                    <FundingRow
                                      label="ARR | Gains Tax"
                                      value={`${isNullOrUndefined(s.annualRateOfReturn) ? '-' : formatPercent(s.annualRateOfReturn, 2)} | ${isNullOrUndefined(s.gainsTaxRate) ? '-' : formatPercent(s.gainsTaxRate, 2)}`}
                                    />
                                  </>
                                )}
                                <FundingRow
                                  label="Total Monthly Income"
                                  value={
                                    isNullOrUndefined(s.monthlyIncomeTotal)
                                      ? '-'
                                      : formatCurrency(s.monthlyIncomeTotal)
                                  }
                                />
                                {!clientHasStartedLtc && (
                                  <>
                                    <FundingRow
                                      label={`Total Principal Invested over ${s.contributionYearCount} year${s.contributionYearCount === 1 ? '' : 's'}`}
                                      value={formatCurrency(s.selfFundingTotalCost)}
                                      className={styles.textRed}
                                    />
                                    <FundingRow
                                      label="LTC Payout"
                                      value={formatLtcCost(remainingProjectedLtcPayout)}
                                      className={styles.textGreenPositive}
                                    />
                                    <FundingRow
                                      label="Non-LTC Payout"
                                      value={formatCurrency(s.selfFundingNonLtcPayout)}
                                      className={styles.textGreenPositive}
                                    />
                                    <FundingRow
                                      label="Total Payout"
                                      value={formatCurrency(s.selfFundingTotalValue)}
                                      className={styles.textGreenPositive}
                                    />
                                    {effectiveRoiDisplayType !== 'hidden' && (
                                      <FundingRow
                                        label={
                                          effectiveRoiDisplayType === 'IRR'
                                            ? 'IRR (Internal Rate of Return) for Modeled Scenario'
                                            : effectiveRoiDisplayType === 'CAGR'
                                              ? `CAGR${cagrDiscountRateLabel} for Modeled Scenario`
                                              : 'Return on Investment for Modeled Scenario'
                                        }
                                        value={formatPercent(
                                          getRoiValue(s, effectiveRoiDisplayType),
                                          2,
                                        )}
                                        className={styles.textGreenPositive}
                                      />
                                    )}
                                  </>
                                )}
                              </div>
                              {!isNullOrUndefined(assetsUsedForPolicy) &&
                                assetsUsedForPolicy > 0 && (
                                  <div className={styles.fundingFootnote}>
                                    Part of your initial assets (
                                    {formatCurrency(assetsUsedForPolicy)}) was used to fund the LTC
                                    policy. The amounts above show the remainder from this source
                                    available for LTC, after growth and taxes.
                                  </div>
                                )}
                            </div>
                          );
                        }

                        if (source.type === 'annuity') {
                          const a = source.data;
                          return (
                            <div
                              key="annuity"
                              className={styles.fundingCard}
                            >
                              <div className={styles.fundingHeaderSection}>
                                <div className={styles.fundingCardHeader}>
                                  <div className={styles.sectionIcon}>
                                    <Banknote size={16} />
                                  </div>
                                  <h3 className={styles.fundingCardTitle}>Annuity</h3>
                                </div>

                                <div className={styles.fundingHeaderGrid}>
                                  <div className={styles.fundingHeaderItem}>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderLabel,
                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                      )}
                                    >
                                      Annual Payout
                                    </div>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderValue,
                                        'text-2xl font-bold',
                                      )}
                                    >
                                      {formatCurrency(a.annuityAnnualPayOut)}
                                    </div>
                                  </div>
                                  <div className={styles.fundingHeaderItem}>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderLabel,
                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                      )}
                                    >
                                      Projected LTC Payout
                                    </div>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderValue,
                                        styles.primaryValue,
                                        'text-2xl font-bold',
                                      )}
                                    >
                                      {formatLtcCost(a.annuityProjectedCostCoverage ?? 0)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className={styles.fundingRowsContainer}>
                                <FundingRow
                                  label="LTC Annual Payout"
                                  value={
                                    isNullOrUndefined(a.annuityAnnualPayoutForLtc)
                                      ? '-'
                                      : formatCurrency(a.annuityAnnualPayoutForLtc)
                                  }
                                />
                                <FundingRow
                                  label="LTC Payout Period"
                                  value={
                                    isNullOrUndefined(a.annuityAnnualPayoutForLtcPeriodYears)
                                      ? '-'
                                      : `${formatInteger(a.annuityAnnualPayoutForLtcPeriodYears)} years`
                                  }
                                />
                                <FundingRow
                                  label="Expected Payout Duration"
                                  value={`Ages ${formatInteger(a.annuityExpectedPaymentStartAge)}-${formatInteger(a.annuityExpectedPaymentEndAge)}`}
                                />
                                <FundingRow
                                  label="Purchase Price"
                                  value={formatCurrency(a.annuityPurchasePrice)}
                                  className={styles.textRed}
                                />
                                <FundingRow
                                  label="LTC Coverage"
                                  value={formatLtcCost(a.annuityProjectedCostCoverage ?? 0)}
                                  className={styles.textGreenPositive}
                                />
                                <FundingRow
                                  label="Non-LTC Payout"
                                  value={formatCurrency(a.annuityNonLtcPayout)}
                                  className={styles.textGreenPositive}
                                />
                                <FundingRow
                                  label="Total Payout"
                                  value={formatCurrency(a.annuityTotalValue)}
                                  className={styles.textGreenPositive}
                                />
                                {effectiveRoiDisplayType !== 'hidden' && (
                                  <FundingRow
                                    label={
                                      effectiveRoiDisplayType === 'IRR'
                                        ? 'IRR (Internal Rate of Return) for Modeled Scenario'
                                        : effectiveRoiDisplayType === 'CAGR'
                                          ? `CAGR${cagrDiscountRateLabel} for Modeled Scenario`
                                          : 'Return on Investment for Modeled Scenario'
                                    }
                                    value={formatPercent(
                                      getRoiValue(a, effectiveRoiDisplayType),
                                      2,
                                    )}
                                    className={styles.textGreenPositive}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        }

                        if (source.type === 'policy') {
                          const p = source.data;
                          return (
                            <div
                              key={`policy-${idx}`}
                              className={styles.fundingCard}
                            >
                              <div className={styles.fundingHeaderSection}>
                                <div className={styles.fundingCardHeader}>
                                  <div className={styles.sectionIcon}>
                                    <ShieldCheck size={16} />
                                  </div>
                                  <h3 className={styles.fundingCardTitle}>
                                    {p.policyType
                                      ? flags.nycLifeDemo
                                        ? PRODUCTS_BY_POLICY_TYPE[p.policyType] ||
                                          fundingPolicyTypeDefs[p.policyType].label
                                        : fundingPolicyTypeDefs[p.policyType].label
                                      : 'LTC Insurance Policy'}
                                  </h3>
                                </div>

                                <div className={styles.fundingHeaderGrid}>
                                  <div className={styles.fundingHeaderItem}>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderLabel,
                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                      )}
                                    >
                                      {clientHasStartedLtc
                                        ? 'Remaining Benefit'
                                        : checkPolicyCalculationTypeEquals(p.policyType, [
                                              FundingPolicyType.hybridLifeInsurance,
                                              FundingPolicyType.lifeInsuranceWithRider,
                                            ])
                                          ? 'Death Benefit'
                                          : 'Max Benefit'}
                                    </div>
                                    <div className={styles.fundingHeaderValue}>
                                      {isNullOrUndefined(p.policyMaximumBenefitAmount)
                                        ? 'Unlimited'
                                        : clientHasStartedLtc
                                          ? formatCurrency(
                                              p.policyMaximumBenefitAmount +
                                                (p.policyContinuationBenefitAmount ?? 0) -
                                                (p.policyBenefitUtilizedToDate ?? 0),
                                            )
                                          : formatCurrency(p.policyMaximumBenefitAmount)}
                                    </div>
                                  </div>
                                  <div className={styles.fundingHeaderItem}>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderLabel,
                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                      )}
                                    >
                                      Projected LTC Payout
                                    </div>
                                    <div
                                      className={cn(
                                        styles.fundingHeaderValue,
                                        styles.primaryValue,
                                        'text-2xl font-bold',
                                      )}
                                    >
                                      {formatCurrency(
                                        clientHasStartedLtc
                                          ? p.policyCoverageOfRemainingLtcCosts
                                          : p.projectedCostCoverage,
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className={styles.fundingRowsContainer}>
                                {checkPolicyCalculationTypeEquals(
                                  p.policyType,
                                  FundingPolicyType.hybridLifeInsurance,
                                ) && (
                                  <FundingRow
                                    label="COB Amount"
                                    value={
                                      isNullOrUndefined(p.policyContinuationBenefitAmount)
                                        ? 'Unlimited'
                                        : formatCurrency(p.policyContinuationBenefitAmount)
                                    }
                                  />
                                )}

                                {checkPolicyCalculationTypeEquals(p.policyType, [
                                  FundingPolicyType.hybridLifeInsurance,
                                  FundingPolicyType.lifeInsuranceWithRider,
                                ]) && (
                                  <FundingRow
                                    label="Guaranteed Death Benefit"
                                    value={formatCurrency(
                                      p.policyMinimumGuaranteedBenefitAmount ?? 0,
                                    )}
                                  />
                                )}

                                <FundingRow
                                  label="Policy Purchase Year"
                                  value={
                                    isNullOrUndefined(p.policyPremiumStartYear)
                                      ? '-'
                                      : formatInteger(p.policyPremiumStartYear)
                                  }
                                />
                                <FundingRow
                                  label="Home/Facility Waiting Period"
                                  value={`${formatThousands(p.homeCareWaitingPeriodDays ?? 0)}/${formatThousands(p.facilityCareWaitingPeriodDays ?? 0)} days`}
                                />
                                <FundingRow
                                  label="Monthly Premium"
                                  value={
                                    isNullOrUndefined(p.policyPremiumMonthlyCost)
                                      ? '-'
                                      : formatCurrency(p.policyPremiumMonthlyCost)
                                  }
                                />
                                {p.policyPremiumIncreaseRate && (
                                  <FundingRow
                                    label="Premium Increase Rate"
                                    value={formatPercent(p.policyPremiumIncreaseRate)}
                                  />
                                )}
                                {p.policyLimitedPayYears && (
                                  <FundingRow
                                    label="Limited Pay Period"
                                    value={`${formatThousands(p.policyLimitedPayYears)} years`}
                                  />
                                )}
                                <FundingRow
                                  label="Lump-Sum Payment"
                                  value={
                                    isNullOrUndefined(p.policyLumpSumPayment)
                                      ? '-'
                                      : formatThousands(p.policyLumpSumPayment)
                                  }
                                />
                                <FundingRow
                                  label="Home Care Max*"
                                  value={
                                    isNullOrUndefined(p.dailyHomeBenefitAmount)
                                      ? '-'
                                      : `${formatCurrency(p.dailyHomeBenefitAmount)}/day`
                                  }
                                />
                                <FundingRow
                                  label="Independent Living Max*"
                                  value={
                                    isNullOrUndefined(p.dailyIndependentLivingBenefitAmount)
                                      ? '-'
                                      : `${formatCurrency(p.dailyIndependentLivingBenefitAmount)}/day`
                                  }
                                />
                                <FundingRow
                                  label="Assisted Living Max*"
                                  value={
                                    isNullOrUndefined(p.dailyAssistedLivingBenefitAmount)
                                      ? '-'
                                      : `${formatCurrency(p.dailyAssistedLivingBenefitAmount)}/day`
                                  }
                                />
                                <FundingRow
                                  label="Nursing Home Max*"
                                  value={
                                    isNullOrUndefined(p.dailyNursingHomeBenefitAmount)
                                      ? '-'
                                      : `${formatCurrency(p.dailyNursingHomeBenefitAmount)}/day`
                                  }
                                />
                                <div className={styles.fundingFootnote}>
                                  * Daily maximum estimated by converting a monthly maximum to a
                                  daily value - dividing by 30. Some policies use a single maximum
                                  across care settings.
                                </div>
                                <FundingRow
                                  label="Inflation Protection"
                                  value={(() => {
                                    // Check if we have a general inflation protection value
                                    if (
                                      !isNullOrUndefined(p.policyInflationProtection) &&
                                      p.policyInflationProtection !== 0
                                    ) {
                                      return `${formatPercent(p.policyInflationProtection, 2)} (${p.isSimpleInflationProtection ? 'Simple' : 'Compound'})`;
                                    }

                                    // Check if we have separate AOB/COB inflation values
                                    const hasAOB =
                                      !isNullOrUndefined(p.policyInflationProtectionOnFaceAmount) &&
                                      p.policyInflationProtectionOnFaceAmount !== 0;
                                    const hasCOB =
                                      !isNullOrUndefined(p.policyInflationProtectionOnCOBAmount) &&
                                      p.policyInflationProtectionOnCOBAmount !== 0;

                                    if (hasAOB && hasCOB) {
                                      return `${formatPercent(p.policyInflationProtectionOnFaceAmount, 2)} AOB, ${formatPercent(p.policyInflationProtectionOnCOBAmount, 2)} COB`;
                                    } else if (hasAOB) {
                                      return `${formatPercent(p.policyInflationProtectionOnFaceAmount, 2)} AOB`;
                                    } else if (hasCOB) {
                                      return `${formatPercent(p.policyInflationProtectionOnCOBAmount, 2)} COB`;
                                    }

                                    return '-';
                                  })()}
                                />
                                {p.policyInflationProtectionYears && (
                                  <FundingRow
                                    label="Inflation Protection Duration"
                                    value={`${formatThousands(p.policyInflationProtectionYears)} years`}
                                  />
                                )}
                                <FundingRow
                                  label="Payment Type"
                                  value={
                                    isNullOrUndefined(p.isIndemnityPolicyPayment) ||
                                    p.isIndemnityPolicyPayment === false
                                      ? 'Reimbursement'
                                      : 'Indemnity'
                                  }
                                />
                                <FundingRow
                                  label="Premiums Paid"
                                  value={formatCurrency(p.policyTotalCost)}
                                  className={styles.textRed}
                                />
                                <FundingRow
                                  label="LTC Coverage"
                                  value={formatLtcCost(
                                    (clientHasStartedLtc
                                      ? p.policyCoverageOfRemainingLtcCosts
                                      : p.projectedCostCoverage) ?? 0,
                                  )}
                                  className={styles.textGreenPositive}
                                />
                                <FundingRow
                                  label="Non-LTC Payout"
                                  value={formatCurrency(p.policyNonLtcPayout)}
                                  className={styles.textGreenPositive}
                                />
                                <FundingRow
                                  label="Total Payout"
                                  value={formatCurrency(p.policyTotalValue)}
                                  className={styles.textGreenPositive}
                                />
                                {effectiveRoiDisplayType !== 'hidden' && (
                                  <FundingRow
                                    label={
                                      effectiveRoiDisplayType === 'IRR'
                                        ? 'IRR (Internal Rate of Return) for Modeled Scenario'
                                        : effectiveRoiDisplayType === 'CAGR'
                                          ? `CAGR${cagrDiscountRateLabel} for Modeled Scenario`
                                          : 'Return on Investment for Modeled Scenario'
                                    }
                                    value={formatPercent(
                                      getRoiValue(p, effectiveRoiDisplayType),
                                      2,
                                    )}
                                    className={styles.textGreenPositive}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })}
                    </div>
                  ))}
                </div>

                {/* COMBINED SUMMARY */}
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>
                    <Coins size={20} />
                  </div>
                  <div className={styles.sectionTitle}>Combined Funding Summary</div>
                </div>

                <div className={styles.summaryContainer}>
                  <div
                    className={cn(
                      styles.summaryRow,
                      effectiveRoiDisplayType === 'hidden' && styles.summaryRowNoRoi,
                    )}
                  >
                    <div className={styles.summaryItem}>
                      <div className={styles.summaryLabel}>TOTAL INVESTED*</div>
                      <div className={styles.summaryValue}>
                        {formatCurrency(combinedFundingTotalCost ?? 0)}
                      </div>
                    </div>
                    <div className={styles.summaryItem}>
                      <div className={styles.summaryLabel}>LTC COVERAGE*</div>
                      <div className={styles.summaryValue}>
                        {formatLtcCost(combinedProjectedCostCoverage ?? 0)}
                      </div>
                    </div>
                    <div className={styles.summaryItem}>
                      <div className={styles.summaryLabel}>NON-LTC PAYOUT*</div>
                      <div className={styles.summaryValue}>
                        {formatCurrency(combinedNonLtcPayout ?? 0)}
                      </div>
                    </div>
                    <div className={styles.summaryItem}>
                      <div className={styles.summaryLabel}>COVERAGE %*</div>
                      <div className={styles.summaryValue}>
                        {formatPercent(combinedProjectedCostCoveragePercent ?? 0, 0)}
                      </div>
                    </div>
                    {effectiveRoiDisplayType !== 'hidden' && (
                      <div className={cn(styles.summaryItem, styles.summaryItemDark)}>
                        <div className={styles.summaryLabel}>
                          COMBINED{' '}
                          {effectiveRoiDisplayType === 'IRR'
                            ? 'IRR'
                            : effectiveRoiDisplayType === 'CAGR'
                              ? `CAGR${cagrDiscountRateLabel}`
                              : 'ROI'}
                          *
                        </div>
                        <div className={styles.summaryValue}>
                          {formatPercent(
                            getRoiValue(client.fundingSources, effectiveRoiDisplayType) ?? 0,
                            2,
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.summaryDisclaimer}>
                    * The values are representative of the modeled scenario and are not guaranteed.
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <div className={styles.pageFooterSpacer} />
                <FundingDisclosure clientFirstName={client.clientFirstName} />
                <div className={styles.pageFooterSafetySpacer} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
});

OnboardingPdfReportV2.displayName = 'OnboardingPdfReportV2';

function FundingDisclosure({ clientFirstName }: { clientFirstName: string }) {
  return (
    <div className={styles.disclosureContainer}>
      <div className={styles.disclosureTitle}>Disclosures</div>
      <p className={styles.disclosureText}>
        {clientFirstName}'s projected LTC cost and age they will need LTC are predictions from
        models trained on other families' LTC journeys. These predictions are estimates and are not
        guaranteed. {clientFirstName}'s LTC costs may be higher or lower, and may begin sooner or
        later than what is shown here. There is no guarantee that a plan built on these numbers will
        cover {clientFirstName}'s costs.
      </p>
      <p className={styles.disclosureText}>
        The investing and insurance information provided on this page is for educational purposes
        only. Waterlily Caregiving, Inc. does not offer advisory or brokerage services, nor does it
        recommend or advise users to buy or sell particular stocks, securities insurance policies,
        or other financial products. Any information entered about an insurance policy is a subset
        of policy terms and can only be used to show an estimate of costs covered by a policy. Any
        policy will likely have terms that limit coverage beyond what is considered here.
      </p>
      <p className={styles.disclosureText}>
        Additional disclaimers and disclosures on our models can be found on our site:{' '}
        <a
          href="https://www.waterlily.com/planning-for-yourself/disclosures-disclaimers"
          className={styles.disclosureLink}
        >
          waterlily.com/planning-for-yourself/disclosures-disclaimers
        </a>
        .
      </p>
    </div>
  );
}

function FundingRow({
  label,
  value,
  className,
}: {
  label: ReactNode;
  value: string | number;
  className?: string;
}) {
  return (
    <div className={styles.fundingRow}>
      <span className={styles.fundingRowLabel}>{label}</span>
      <span className={cn(styles.fundingRowValue, className)}>{value}</span>
    </div>
  );
}
